import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError, voidRecord } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Page, PageHeader, StatusBadge, Modal, Field, inputCls, PrimaryButton, EmptyState } from "@/components/lims";
import { canManageClients, canDeleteClients } from "@/lib/roles";
import { Plus, Buildings, Trash, PencilSimple } from "@phosphor-icons/react";

const INDUSTRIES = ["Pharma", "Biotech", "Academic", "Clinical", "Environmental", "Food & Beverage", "Other"];
const STATUSES = ["Active", "Inactive", "Credit Hold"];
const empty = { legal_name: "", trading_name: "", industry: "Biotech", email: "", phone: "", address: "", status: "Active", tat_target_days: "" };

export default function Clients() {
  const { user } = useAuth();
  const canManage = canManageClients(user);
  const canDelete = canDeleteClients(user);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/clients");
    setClients(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...empty, ...c }); setOpen(true); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, tat_target_days: form.tat_target_days ? Number(form.tat_target_days) : null };
      if (editing) { await api.put(`/clients/${editing.id}`, payload); toast.success("Client updated"); }
      else { await api.post("/clients", payload); toast.success("Client added"); }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!(await voidRecord(`/clients/${c.id}`, c.legal_name))) return;
    toast.success("Client voided"); load();
  };

  return (
    <Page>
      <PageHeader title="Clients" subtitle="Customer organizations and commercial relationships"
        action={canManage && <PrimaryButton data-testid="new-client-button" onClick={openNew}><Plus size={18} /> Add Client</PrimaryButton>} />

      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
        {loading ? (
          <div className="p-8 space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-10 bg-slate-100 rounded animate-pulse" />)}</div>
        ) : clients.length === 0 ? (
          <EmptyState icon={Buildings} title="No clients yet" subtitle="Add a client to start receiving orders" />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3 font-semibold">Code</th>
                <th className="px-5 py-3 font-semibold">Name</th>
                <th className="px-5 py-3 font-semibold">Industry</th>
                <th className="px-5 py-3 font-semibold">Contact</th>
                <th className="px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3 font-semibold">TAT target</th>
                <th className="px-5 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} data-testid={`client-row-${c.client_code}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
                  <td className="px-5 py-3 font-mono text-xs text-slate-500">{c.client_code}</td>
                  <td className="px-5 py-3 font-medium text-slate-900">{c.legal_name}<div className="text-xs text-slate-400 font-normal">{c.trading_name}</div></td>
                  <td className="px-5 py-3 text-slate-600">{c.industry}</td>
                  <td className="px-5 py-3 text-slate-500 text-xs">{c.email || "—"}<div>{c.phone}</div></td>
                  <td className="px-5 py-3"><StatusBadge status={c.status} /></td>
                  <td className="px-5 py-3 text-slate-500">{c.tat_target_days ? `${c.tat_target_days} days` : "Lab default"}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {canManage && <button data-testid={`edit-client-${c.client_code}`} onClick={() => openEdit(c)} className="p-1.5 rounded-md text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-colors"><PencilSimple size={16} /></button>}
                      {canDelete && <button onClick={() => remove(c)} className="p-1.5 rounded-md text-slate-400 hover:text-status-critical hover:bg-status-critical/10 transition-colors"><Trash size={16} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Client" : "Add Client"} testid="client-modal">
        <form onSubmit={submit} className="space-y-4">
          <Field label="Legal name"><input data-testid="client-name-input" required value={form.legal_name} onChange={set("legal_name")} className={inputCls} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Trading name"><input value={form.trading_name} onChange={set("trading_name")} className={inputCls} /></Field>
            <Field label="Industry"><select value={form.industry} onChange={set("industry")} className={inputCls}>{INDUSTRIES.map((i) => <option key={i}>{i}</option>)}</select></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Email"><input type="email" value={form.email} onChange={set("email")} className={inputCls} /></Field>
            <Field label="Phone"><input value={form.phone} onChange={set("phone")} className={inputCls} /></Field>
          </div>
          <Field label="Address"><textarea value={form.address} onChange={set("address")} rows={2} className={inputCls} /></Field>
          <Field label="Status"><select value={form.status} onChange={set("status")} className={inputCls}>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select></Field>
          <Field label="TAT target (days)"><input type="number" min="1" max="365" value={form.tat_target_days ?? ""} onChange={set("tat_target_days")} className={inputCls} placeholder="Use laboratory default" /></Field>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100 transition-colors">Cancel</button>
            <PrimaryButton data-testid="save-client-button" disabled={saving}>{editing ? "Save" : "Add"}</PrimaryButton>
          </div>
        </form>
      </Modal>
    </Page>
  );
}
